import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IMAGE_TITLE_MESSAGE } from '@/blocks/preview';
import { INSPECT_CLASS } from '@/appearance/inspect/styles';
import { frameEditable } from '@components/preview/editGates';
import { useMediaTitle } from './MediaFrame';

const CAPTION_CLASS = 'media-caption uppercase tracking-[0.15em] text-text';

/** The appearance preview iframe, where the parent editor is listening. */
export function imageTitleEditing() {
    if (typeof window === 'undefined') return false;
    if (window.parent === window) return false;
    return frameEditable();
}

function useInspectActive() {
    const [active, setActive] = useState(() => (
        typeof document !== 'undefined' && document.body.classList.contains(INSPECT_CLASS)
    ));
    useEffect(() => {
        const sync = () => setActive(document.body.classList.contains(INSPECT_CLASS));
        sync();
        const observer = new MutationObserver(sync);
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);
    return active;
}

// The frame's copy of file descriptions is from when it loaded. The latest
// title per image, typed here or sent by the editor (including after a save),
// wins so reopening an image never shows the old caption.
const latestTitles = new Map();
let titleListener = false;

function trackTitles() {
    if (titleListener || !imageTitleEditing()) return;
    titleListener = true;
    window.addEventListener('message', (event) => {
        if (event.origin !== window.location.origin) return;
        const data = event.data;
        if (!data || data.type !== IMAGE_TITLE_MESSAGE || typeof data.title !== 'string' || !data.imageId) return;
        latestTitles.set(data.imageId, data.title);
    });
}

trackTitles();

function titleFor(imageId, description) {
    return imageId && latestTitles.has(imageId) ? latestTitles.get(imageId) : (description || '');
}

/**
 * The caption under an open image. In the appearance preview it is a text field,
 * and it follows title edits posted from the parent panel.
 */
export default function ImageCaption({
    description = '',
    imageId,
    className = CAPTION_CLASS,
    style,
    chrome = true,
}) {
    const title = useMediaTitle();
    const editing = imageTitleEditing();
    const inspecting = useInspectActive();
    // Inspect moves the title; typing waits until Inspect is off.
    const canType = editing && !inspecting;
    if (editing) trackTitles();
    const ref = useRef(null);
    const [text, setText] = useState(() => (editing ? titleFor(imageId, description) : (description || '')));

    useEffect(() => {
        setText(editing ? titleFor(imageId, description) : (description || ''));
    }, [description, imageId, editing]);

    useLayoutEffect(() => {
        const node = ref.current;
        if (!node || !canType || document.activeElement === node) return;
        if (node.textContent !== text) node.textContent = text;
    }, [text, canType]);

    useEffect(() => {
        if (!canType) return undefined;
        const onMessage = (event) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data;
            if (!data || data.type !== IMAGE_TITLE_MESSAGE || typeof data.title !== 'string') return;
            if (imageId && data.imageId && data.imageId !== imageId) return;
            if (document.activeElement === ref.current) return;
            setText(data.title);
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [canType, imageId]);

    const captionClass = chrome && title.active ? '' : className;
    const captionStyle = {
        ...(chrome ? title.style : {}),
        ...style,
    };
    const moduleProps = chrome ? {
        'data-header-module': title.id,
        'data-lock-ratio': '0',
    } : {};

    if (!canType) {
        // Keep an empty title visible while Inspect is on so it can be moved.
        if (!description && !text && !inspecting) return null;
        return (
            <span
                className={captionClass}
                style={captionStyle}
                {...moduleProps}
            >
                {text || description || (inspecting ? 'Title' : '')}
            </span>
        );
    }

    return (
        <span
            ref={ref}
            {...moduleProps}
            className={`${captionClass} cursor-text empty:before:content-['Title'] empty:before:opacity-40`}
            style={{ outline: 'none', minWidth: '4ch', ...captionStyle }}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-label="Image title"
            spellCheck={false}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === 'Enter') {
                    event.preventDefault();
                    event.currentTarget.blur();
                }
            }}
            onInput={(event) => {
                const next = event.currentTarget.textContent || '';
                if (imageId) latestTitles.set(imageId, next);
                window.parent.postMessage({
                    type: IMAGE_TITLE_MESSAGE,
                    imageId,
                    title: next,
                }, window.location.origin);
            }}
            onBlur={(event) => {
                const next = (event.currentTarget.textContent || '').trim();
                if (imageId) latestTitles.set(imageId, next);
                setText(next);
                window.parent.postMessage({
                    type: IMAGE_TITLE_MESSAGE,
                    imageId,
                    title: next,
                    commit: true,
                }, window.location.origin);
            }}
        />
    );
}

ImageCaption.propTypes = {
    description: PropTypes.string,
    chrome: PropTypes.bool,
    imageId: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
};
